export default function TechDocs() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-800">
      <div className="max-w-5xl mx-auto py-10 px-4 sm:px-6 lg:px-8">
      <header className="text-center mb-10">
        <h1 className="text-4xl font-bold text-slate-100 mb-3">API Tech Docs</h1>
        <p className="text-lg text-slate-300">
          Everything you need to ship EmotionWise into your stack fast.
        </p>
      </header>

      <section className="bg-slate-900/85 backdrop-blur-sm rounded-2xl border border-slate-700 shadow-lg p-6 sm:p-8 mb-6">
        <h2 className="text-2xl font-semibold text-slate-100 mb-3">1. Authenticate Requests</h2>
        <p className="text-slate-300 mb-4">
          Use X-API-Key for backend integrations. This is the recommended authentication method for external developers.
        </p>
        <ul className="list-disc pl-6 text-slate-300 space-y-2">
          <li><code>X-API-Key: &lt;api_key&gt;</code> for external integrations.</li>
        </ul>
      </section>

      <section className="bg-slate-900/85 backdrop-blur-sm rounded-2xl border border-slate-700 shadow-lg p-6 sm:p-8 mb-6">
        <h2 className="text-2xl font-semibold text-slate-100 mb-3">Pricing Snapshot</h2>
        <p className="text-slate-300 mb-4">
          Start small, then scale usage without changing your integration.
        </p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left text-slate-200 border border-slate-700 rounded-xl overflow-hidden">
            <thead className="bg-slate-800 text-slate-100">
              <tr>
                <th className="px-4 py-3 font-semibold">Plan</th>
                <th className="px-4 py-3 font-semibold">Price</th>
                <th className="px-4 py-3 font-semibold">Monthly Calls</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-t border-slate-700">
                <td className="px-4 py-3">Free</td>
                <td className="px-4 py-3">$0</td>
                <td className="px-4 py-3">1,000</td>
              </tr>
              <tr className="border-t border-slate-700">
                <td className="px-4 py-3">Starter</td>
                <td className="px-4 py-3">$9.99/mo</td>
                <td className="px-4 py-3">10,000</td>
              </tr>
              <tr className="border-t border-slate-700">
                <td className="px-4 py-3">Pro</td>
                <td className="px-4 py-3">$29.99/mo</td>
                <td className="px-4 py-3">25,000</td>
              </tr>
              <tr className="border-t border-slate-700">
                <td className="px-4 py-3">Scale</td>
                <td className="px-4 py-3">$99/mo</td>
                <td className="px-4 py-3">250,000</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <section className="bg-slate-900/85 backdrop-blur-sm rounded-2xl border border-slate-700 shadow-lg p-6 sm:p-8 mb-6">
        <h2 className="text-2xl font-semibold text-slate-100 mb-3">2. Run Your First Detection</h2>
        <p className="text-slate-300 mb-2">Endpoint: <code>POST /api/v1/tools/emotion-detector</code></p>
        <p className="text-slate-300 mb-4">Message length: <code>min 1</code> and <code>max 1000</code> characters.</p>
        <div className="bg-gray-900 text-gray-100 rounded-xl p-4 overflow-x-auto text-sm">
          <pre>{`curl -X POST "https://api.emotionwise.ai/api/v1/tools/emotion-detector" \\
  -H "Content-Type: application/json" \\
  -H "X-API-Key: YOUR_API_KEY" \\
  -d '{
    "message": "I am happy but a bit nervous",
    "context": "daily journal"
  }'`}</pre>
        </div>
      </section>

      <section className="bg-slate-900/85 backdrop-blur-sm rounded-2xl border border-slate-700 shadow-lg p-6 sm:p-8 mb-6">
        <h2 className="text-2xl font-semibold text-slate-100 mb-3">Supported Emotions by Language</h2>
        <p className="text-slate-300 mb-4">
          EmotionWise uses language-specific models optimized for accuracy. The detected language is returned in every response.
        </p>

        <h3 className="text-lg font-semibold text-slate-200 mb-2">English — 28 emotions</h3>
        <p className="text-sm text-slate-400 mb-2">Model: <code>bert-base-go-emotion</code> (multi-label, sigmoid)</p>
        <div className="bg-slate-800/80 border border-slate-700 rounded-xl p-4 mb-6 text-sm text-slate-200 overflow-x-auto">
          <code>admiration, amusement, anger, annoyance, approval, caring, confusion, curiosity, desire, disappointment, disapproval, disgust, embarrassment, excitement, fear, gratitude, grief, joy, love, nervousness, optimism, pride, realization, relief, remorse, sadness, surprise, neutral</code>
        </div>

        <h3 className="text-lg font-semibold text-slate-200 mb-2">Spanish — 7 emotions</h3>
        <p className="text-sm text-slate-400 mb-2">Model: <code>robertuito-emotion-analysis</code> (single-label, softmax)</p>
        <div className="bg-slate-800/80 border border-slate-700 rounded-xl p-4 mb-2 text-sm text-slate-200 overflow-x-auto">
          <code>joy, sadness, anger, surprise, disgust, fear, neutral</code>
        </div>
        <p className="text-xs text-slate-400">Unsupported languages fall back to the English model.</p>
      </section>

      <section className="bg-slate-900/85 backdrop-blur-sm rounded-2xl border border-slate-700 shadow-lg p-6 sm:p-8 mb-6">
        <h2 className="text-2xl font-semibold text-slate-100 mb-3">3. Send Optional Feedback (Recommended)</h2>
        <p className="text-slate-300 mb-4">
          Feedback is optional, but it is the fastest way to improve quality for your domain over time.
        </p>
        <p className="text-slate-300 mb-4">Endpoint: <code>POST /api/v1/feedback/submit</code></p>
        <p className="text-slate-300 mb-2">Valid emotion labels accepted by the system depend on the language:</p>

        <p className="text-sm text-slate-400 mt-3 mb-1">English (<code>language_code: "en"</code>) — 28 labels:</p>
        <div className="bg-slate-800/80 border border-slate-700 rounded-xl p-4 mb-3 text-sm text-slate-200 overflow-x-auto">
          <code>["admiration", "amusement", "anger", "annoyance", "approval", "caring", "confusion", "curiosity", "desire", "disappointment", "disapproval", "disgust", "embarrassment", "excitement", "fear", "gratitude", "grief", "joy", "love", "nervousness", "optimism", "pride", "realization", "relief", "remorse", "sadness", "surprise", "neutral"]</code>
        </div>

        <p className="text-sm text-slate-400 mb-1">Spanish (<code>language_code: "es"</code>) — 7 labels:</p>
        <div className="bg-slate-800/80 border border-slate-700 rounded-xl p-4 mb-2 text-sm text-slate-200 overflow-x-auto">
          <code>["joy", "sadness", "anger", "surprise", "disgust", "fear", "neutral"]</code>
        </div>
        <p className="text-xs text-slate-400 mb-4">Include <code>language_code</code> in your feedback payload so the correct label set is used for validation. If omitted, the language is auto-detected from the text.</p>
        <div className="bg-gray-900 text-gray-100 rounded-xl p-4 overflow-x-auto text-sm">
          <pre>{`curl -X POST "https://api.emotionwise.ai/api/v1/feedback/submit" \\
  -H "Content-Type: application/json" \\
  -H "X-API-Key: YOUR_API_KEY" \\
  -d '{
    "text": "I am happy but a bit nervous",
    "predicted_emotions": ["joy", "nervousness"],
    "suggested_emotions": ["optimism"],
    "predicted_sarcasm": false,
    "sarcasm_feedback": null,
    "comment": "Pretty accurate",
    "language_code": "en"
  }'`}</pre>
        </div>
      </section>

      <section className="bg-slate-900/85 backdrop-blur-sm rounded-2xl border border-slate-700 shadow-lg p-6 sm:p-8 mb-6">
        <h2 className="text-2xl font-semibold text-slate-100 mb-3">4. Wrappers</h2>
        <p className="text-slate-300 mb-4">
          Prefer SDK-style integration? Start with the community Python wrapper:
        </p>
        <div className="rounded-xl border border-slate-700 bg-slate-800/60 p-4">
          <ul className="list-disc pl-5 space-y-3">
            <li>
              <a
                href="https://github.com/timexicali/emotionwise-python-wrapper"
                target="_blank"
                rel="noopener noreferrer"
                className="text-indigo-300 hover:text-indigo-200 underline break-all"
              >
                emotionwise-python-wrapper (GitHub)
              </a>
              <p className="text-sm text-slate-300 mt-1">
                Wrapper maintained for Python integrations using EmotionWise API keys.
              </p>
            </li>
            <li>
              <a
                href="https://github.com/timexicali/emotionwise-php-wrapper"
                target="_blank"
                rel="noopener noreferrer"
                className="text-indigo-300 hover:text-indigo-200 underline break-all"
              >
                emotionwise-php-wrapper (GitHub)
              </a>
              <p className="text-sm text-slate-300 mt-1">
                Wrapper maintained for PHP integrations using EmotionWise API keys.
              </p>
            </li>
          </ul>
        </div>
      </section>

      <section className="bg-slate-900/85 backdrop-blur-sm rounded-2xl border border-slate-700 shadow-lg p-6 sm:p-8 mb-6">
        <h2 className="text-2xl font-semibold text-slate-100 mb-3">5. API Error Reference</h2>
        <p className="text-slate-300 mb-4">
          Common error codes you should handle in client integrations.
        </p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left text-slate-200 border border-slate-700 rounded-xl overflow-hidden">
            <thead className="bg-slate-800 text-slate-100">
              <tr>
                <th className="px-4 py-3 font-semibold">Status</th>
                <th className="px-4 py-3 font-semibold">Meaning</th>
                <th className="px-4 py-3 font-semibold">Typical Cause</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-t border-slate-700">
                <td className="px-4 py-3"><code>401</code></td>
                <td className="px-4 py-3">Unauthorized</td>
                <td className="px-4 py-3">Missing or invalid API key.</td>
              </tr>
              <tr className="border-t border-slate-700">
                <td className="px-4 py-3"><code>403</code></td>
                <td className="px-4 py-3">Forbidden</td>
                <td className="px-4 py-3">API key is inactive or not allowed for this route.</td>
              </tr>
              <tr className="border-t border-slate-700">
                <td className="px-4 py-3"><code>429</code></td>
                <td className="px-4 py-3">Too Many Requests</td>
                <td className="px-4 py-3">Plan quota or rate limit exceeded.</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className="mt-4 bg-gray-900 text-gray-100 rounded-xl p-4 overflow-x-auto text-sm">
          <pre>{`// 401 example
{"detail":"Missing API key"}

// 403 example
{"detail":"API key is inactive"}

// 429 example
{"detail":"Quota exceeded for current billing period"}`}</pre>
        </div>
      </section>

      <section className="bg-slate-900/85 backdrop-blur-sm rounded-2xl border border-slate-700 shadow-lg p-6 sm:p-8 mb-6">
        <h2 className="text-2xl font-semibold text-slate-100 mb-3">6. Response Structure</h2>
        <p className="text-slate-300 mb-4">
          Every call to the emotion detector returns the same JSON shape. The emotion labels depend on the detected language.
        </p>

        <div className="overflow-x-auto mb-4">
          <table className="w-full text-sm text-left text-slate-200 border border-slate-700 rounded-xl overflow-hidden">
            <thead className="bg-slate-800 text-slate-100">
              <tr>
                <th className="px-4 py-3 font-semibold">Field</th>
                <th className="px-4 py-3 font-semibold">Type</th>
                <th className="px-4 py-3 font-semibold">Description</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-t border-slate-700">
                <td className="px-4 py-3"><code>session_id</code></td>
                <td className="px-4 py-3">string</td>
                <td className="px-4 py-3">Unique ID for this analysis. Use it to submit feedback.</td>
              </tr>
              <tr className="border-t border-slate-700">
                <td className="px-4 py-3"><code>detected_emotions</code></td>
                <td className="px-4 py-3">string[]</td>
                <td className="px-4 py-3">Top emotions above the confidence threshold (up to 4).</td>
              </tr>
              <tr className="border-t border-slate-700">
                <td className="px-4 py-3"><code>confidence_scores</code></td>
                <td className="px-4 py-3">{`{string: int}`}</td>
                <td className="px-4 py-3">Confidence (0-100) for each detected emotion.</td>
              </tr>
              <tr className="border-t border-slate-700">
                <td className="px-4 py-3"><code>detected_language</code></td>
                <td className="px-4 py-3">string</td>
                <td className="px-4 py-3"><code>"en"</code>, <code>"es"</code>, or <code>"other"</code>. Determines which model and label set was used.</td>
              </tr>
              <tr className="border-t border-slate-700">
                <td className="px-4 py-3"><code>sarcasm_detected</code></td>
                <td className="px-4 py-3">boolean</td>
                <td className="px-4 py-3">Whether sarcasm was detected. English only; always <code>false</code> for Spanish and other.</td>
              </tr>
              <tr className="border-t border-slate-700">
                <td className="px-4 py-3"><code>sarcasm_score</code></td>
                <td className="px-4 py-3">int</td>
                <td className="px-4 py-3">Sarcasm confidence (0-100). English only; <code>0</code> for Spanish and other.</td>
              </tr>
              <tr className="border-t border-slate-700">
                <td className="px-4 py-3"><code>recommendation</code></td>
                <td className="px-4 py-3">string | null</td>
                <td className="px-4 py-3">Optional supportive suggestion based on detected emotions. Localized to detected language.</td>
              </tr>
              <tr className="border-t border-slate-700">
                <td className="px-4 py-3"><code>analysis_mode</code></td>
                <td className="px-4 py-3">string | null</td>
                <td className="px-4 py-3"><code>"full_only"</code> or <code>"contrast_split"</code> if clause analysis was applied.</td>
              </tr>
              <tr className="border-t border-slate-700">
                <td className="px-4 py-3"><code>mixed_signal</code></td>
                <td className="px-4 py-3">boolean | null</td>
                <td className="px-4 py-3"><code>true</code> when both positive and negative emotions are strongly present.</td>
              </tr>
              <tr className="border-t border-slate-700">
                <td className="px-4 py-3"><code>emoji_analysis</code></td>
                <td className="px-4 py-3">object | null</td>
                <td className="px-4 py-3">Present when the message contains recognized emojis. <code>null</code> for text-only messages. See Emoji Analysis below.</td>
              </tr>
            </tbody>
          </table>
        </div>

        <h3 className="text-lg font-semibold text-slate-200 mb-2">Emoji Analysis</h3>
        <p className="text-slate-300 mb-2">
          When a message contains emojis, the response includes an <code>emoji_analysis</code> object with these fields:
        </p>
        <div className="overflow-x-auto mb-4">
          <table className="w-full text-sm text-left text-slate-200 border border-slate-700 rounded-xl overflow-hidden">
            <thead className="bg-slate-800 text-slate-100">
              <tr>
                <th className="px-4 py-3 font-semibold">Field</th>
                <th className="px-4 py-3 font-semibold">Type</th>
                <th className="px-4 py-3 font-semibold">Description</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-t border-slate-700">
                <td className="px-4 py-3"><code>emojis_found</code></td>
                <td className="px-4 py-3">string[]</td>
                <td className="px-4 py-3">List of recognized emojis in the message.</td>
              </tr>
              <tr className="border-t border-slate-700">
                <td className="px-4 py-3"><code>emoji_emotions</code></td>
                <td className="px-4 py-3">{`{string: float}`}</td>
                <td className="px-4 py-3">Emotion scores derived from emojis (0.0-1.0, frequency-based).</td>
              </tr>
              <tr className="border-t border-slate-700">
                <td className="px-4 py-3"><code>emoji_only</code></td>
                <td className="px-4 py-3">boolean</td>
                <td className="px-4 py-3"><code>true</code> if the message contains only emojis (ML model skipped).</td>
              </tr>
              <tr className="border-t border-slate-700">
                <td className="px-4 py-3"><code>contradiction_detected</code></td>
                <td className="px-4 py-3">boolean</td>
                <td className="px-4 py-3"><code>true</code> if emoji sentiment contradicts text sentiment (sarcasm signal).</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p className="text-sm text-slate-400 mb-4">
          Three scenarios: <strong>Emoji-only</strong> messages (e.g. {`"🎉🎉👏👏"`}) skip the ML model and return emoji-derived emotions directly. <strong>Mixed text+emoji</strong> messages run the ML model on the text and emoji analysis separately, with contradiction detection boosting the sarcasm score when polarities oppose. <strong>Text-only</strong> messages return <code>emoji_analysis: null</code>.
        </p>

        <h3 className="text-lg font-semibold text-slate-200 mb-2">Text-only example (English)</h3>
        <div className="bg-gray-900 text-gray-100 rounded-xl p-4 overflow-x-auto text-sm mb-4">
          <pre>{`{
  "session_id": "a1b2c3d4-...",
  "detected_emotions": ["joy", "admiration", "excitement"],
  "confidence_scores": {"joy": 55, "admiration": 35, "excitement": 16},
  "detected_language": "en",
  "sarcasm_detected": false,
  "sarcasm_score": 5,
  "recommendation": "I'm glad you're feeling good...",
  "analysis_mode": null,
  "mixed_signal": null,
  "emoji_analysis": null
}`}</pre>
        </div>

        <h3 className="text-lg font-semibold text-slate-200 mb-2">Text-only example (Spanish)</h3>
        <div className="bg-gray-900 text-gray-100 rounded-xl p-4 overflow-x-auto text-sm mb-4">
          <pre>{`{
  "session_id": "e5f6g7h8-...",
  "detected_emotions": ["sadness"],
  "confidence_scores": {"sadness": 99},
  "detected_language": "es",
  "sarcasm_detected": false,
  "sarcasm_score": 0,
  "recommendation": "Parece que estás triste...",
  "analysis_mode": null,
  "mixed_signal": null,
  "emoji_analysis": null
}`}</pre>
        </div>

        <h3 className="text-lg font-semibold text-slate-200 mb-2">Emoji-only example</h3>
        <div className="bg-gray-900 text-gray-100 rounded-xl p-4 overflow-x-auto text-sm mb-4">
          <pre>{`{
  "session_id": "c3d4e5f6-...",
  "detected_emotions": ["joy", "admiration"],
  "confidence_scores": {"joy": 50, "admiration": 50},
  "detected_language": "en",
  "sarcasm_detected": false,
  "sarcasm_score": 0,
  "recommendation": "...",
  "analysis_mode": null,
  "mixed_signal": null,
  "emoji_analysis": {
    "emojis_found": ["🎉", "🎉", "🎉", "👏", "👏"],
    "emoji_emotions": {"joy": 0.6, "admiration": 0.4},
    "emoji_only": true,
    "contradiction_detected": false
  }
}`}</pre>
        </div>

        <h3 className="text-lg font-semibold text-slate-200 mb-2">Mixed text+emoji with contradiction</h3>
        <div className="bg-gray-900 text-gray-100 rounded-xl p-4 overflow-x-auto text-sm">
          <pre>{`{
  "session_id": "f7g8h9i0-...",
  "detected_emotions": ["admiration"],
  "confidence_scores": {"admiration": 60},
  "detected_language": "en",
  "sarcasm_detected": false,
  "sarcasm_score": 23,
  "recommendation": "...",
  "analysis_mode": null,
  "mixed_signal": null,
  "emoji_analysis": {
    "emojis_found": ["😢", "💔"],
    "emoji_emotions": {"sadness": 1.0},
    "emoji_only": false,
    "contradiction_detected": true
  }
}`}</pre>
        </div>
      </section>

      <section className="bg-slate-900/85 backdrop-blur-sm rounded-2xl border border-slate-700 shadow-lg p-6 sm:p-8 mb-6">
        <h2 className="text-2xl font-semibold text-slate-100 mb-3">7. Batch Emotion Detection</h2>
        <p className="text-slate-300 mb-2">
          Analyze up to <strong>25 messages</strong> in a single request. Each message runs through the full pipeline (language detection, emotion detection, sarcasm, emoji analysis). Auth only (API key required).
        </p>
        <p className="text-slate-300 mb-2">Endpoint: <code>POST /api/v1/tools/emotion-detector/batch</code></p>
        <p className="text-slate-300 mb-4">Rate limit: <code>400 requests/minute</code>. Each message counts individually against your plan quota.</p>

        <h3 className="text-lg font-semibold text-slate-200 mb-2">Request</h3>
        <div className="overflow-x-auto mb-4">
          <table className="w-full text-sm text-left text-slate-200 border border-slate-700 rounded-xl overflow-hidden">
            <thead className="bg-slate-800 text-slate-100">
              <tr>
                <th className="px-4 py-3 font-semibold">Field</th>
                <th className="px-4 py-3 font-semibold">Type</th>
                <th className="px-4 py-3 font-semibold">Description</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-t border-slate-700">
                <td className="px-4 py-3"><code>messages</code></td>
                <td className="px-4 py-3">string[]</td>
                <td className="px-4 py-3">Array of 1-25 messages. Each message: 1-1000 characters.</td>
              </tr>
              <tr className="border-t border-slate-700">
                <td className="px-4 py-3"><code>context</code></td>
                <td className="px-4 py-3">string | null</td>
                <td className="px-4 py-3">Optional context label (e.g. <code>"youtube_comments"</code>, <code>"journal"</code>).</td>
              </tr>
              <tr className="border-t border-slate-700">
                <td className="px-4 py-3"><code>session_id</code></td>
                <td className="px-4 py-3">string | null</td>
                <td className="px-4 py-3">Optional. Auto-generated if omitted. Shared across all messages in the batch.</td>
              </tr>
            </tbody>
          </table>
        </div>

        <h3 className="text-lg font-semibold text-slate-200 mb-2">cURL example</h3>
        <div className="bg-gray-900 text-gray-100 rounded-xl p-4 overflow-x-auto text-sm mb-4">
          <pre>{`curl -X POST "https://api.emotionwise.ai/api/v1/tools/emotion-detector/batch" \\
  -H "Content-Type: application/json" \\
  -H "X-API-Key: YOUR_API_KEY" \\
  -d '{
    "messages": [
      "Excelente Mr Doctor",
      "I am so happy today",
      "\\ud83c\\udf89\\ud83c\\udf89\\ud83c\\udf89\\ud83d\\udc4f\\ud83d\\udc4f",
      "Justicia!!!!!",
      "Everything is great \\ud83d\\ude22\\ud83d\\udc94"
    ],
    "context": "youtube_comments"
  }'`}</pre>
        </div>

        <h3 className="text-lg font-semibold text-slate-200 mb-2">Response</h3>
        <p className="text-slate-300 mb-2">
          Returns a <code>session_id</code> and a <code>results</code> array. Each element in the array has the same structure as the single-detection response (see section 6).
        </p>
        <div className="bg-gray-900 text-gray-100 rounded-xl p-4 overflow-x-auto text-sm mb-4">
          <pre>{`{
  "session_id": "b6895035-...",
  "results": [
    {
      "session_id": "b6895035-...",
      "detected_emotions": ["joy"],
      "confidence_scores": {"joy": 85},
      "sarcasm_detected": false,
      "sarcasm_score": 0,
      "detected_language": "es",
      "recommendation": "...",
      "analysis_mode": null,
      "mixed_signal": null,
      "emoji_analysis": null
    },
    {
      "session_id": "b6895035-...",
      "detected_emotions": ["joy", "excitement"],
      "confidence_scores": {"joy": 69, "excitement": 17},
      "sarcasm_detected": false,
      "sarcasm_score": 8,
      "detected_language": "en",
      "recommendation": "...",
      "analysis_mode": null,
      "mixed_signal": null,
      "emoji_analysis": null
    },
    {
      "session_id": "b6895035-...",
      "detected_emotions": ["joy", "admiration"],
      "confidence_scores": {"joy": 50, "admiration": 50},
      "sarcasm_detected": false,
      "sarcasm_score": 0,
      "detected_language": "en",
      "recommendation": "...",
      "emoji_analysis": {
        "emojis_found": ["\\ud83c\\udf89","\\ud83c\\udf89","\\ud83c\\udf89","\\ud83d\\udc4f","\\ud83d\\udc4f"],
        "emoji_emotions": {"joy": 0.6, "admiration": 0.4},
        "emoji_only": true,
        "contradiction_detected": false
      }
    }
  ]
}`}</pre>
        </div>

        <p className="text-sm text-slate-400">
          Each result in the array has the same structure as the single-detection response, including <code>emoji_analysis</code> when emojis are present (see section 6).
        </p>
      </section>

      <section className="bg-indigo-500/10 border border-indigo-500/30 rounded-2xl p-6">
        <p className="text-sm text-amber-200 mb-2">Sarcasm Beta Note: treat sarcasm fields as experimental signals. English only.</p>
        <p className="text-sm text-indigo-200/90">Policy note: do not use this API as the sole basis for legal, medical, hiring, or safety-critical decisions.</p>
      </section>
      </div>
    </div>
  );
}
